/**
 * "Does this quote cover every failed device?" (trust audit Phase 1; mockup
 * https://claude.ai/artifact/BoCo28jmCc98BrDHgbg416, approved 10/22).
 *
 * One row per failed device that isn't on the quote; a bypass is its own row.
 * Each row needs an answer: Add it to the quote (drafts a "Not priced yet"
 * repair on that device) or Not quoting it… (one line why). Approve & send is
 * refused on the server until every row is answered.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HowItWorks } from "@/components/how-it-works";

export interface CoverageLeg {
    assetId: string;
    leg: "main" | "bypass";
    device: string;
    why: string | null;
    testedAt: string | null;
    covered: "quoted" | "not_quoting" | null;
    notQuotingReason: string | null;
    notQuotingBy: string | null;
}

export function coverageSummary(legs: CoverageLeg[]): string {
    const quoted = legs.filter((l) => l.covered === "quoted").length;
    const waived = legs.filter((l) => l.covered === "not_quoting").length;
    const parts = [`${legs.length} failed ${legs.length === 1 ? "device" : "devices"}`, `${quoted} on the quote`];
    if (waived) parts.push(`${waived} not quoting`);
    return `Quote matches the test: ${parts.join(", ")}.`;
}

function shortDate(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function QuoteCheckView({
    legs,
    editable,
    pending,
    onAdd,
    onNotQuoting,
    onUndo,
}: {
    legs: CoverageLeg[];
    editable: boolean;
    pending?: boolean;
    onAdd: (l: CoverageLeg) => void;
    onNotQuoting: (l: CoverageLeg, reason: string) => void;
    onUndo: (l: CoverageLeg) => void;
}) {
    const [asking, setAsking] = useState<string | null>(null);
    const [reason, setReason] = useState("");
    if (legs.length === 0) return null;
    const open = legs.filter((l) => l.covered === null);
    const waived = legs.filter((l) => l.covered === "not_quoting");
    const key = (l: CoverageLeg) => `${l.assetId}:${l.leg}`;
    return (
        <div className="mb-3 space-y-2" data-testid="quote-check">
            <div className="flex items-center gap-2 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                Does this quote cover every failed device?
                <HowItWorks
                    title="How the quote check works"
                    testId="how-quote-check"
                    steps={[
                        {
                            title: "GWC lists every device that failed and isn't on the quote",
                            text: "A bypass is listed on its own. A main that passed is never listed.",
                            picture: "quote-check-list.png",
                            alt: "The quote check with a failed device ringed",
                        },
                        {
                            title: "For each one, pick an answer",
                            text: "Add it to the quote puts the repair on the quote, marked Not priced yet; pick the kit to price it. Not quoting it… is for when we're not doing that repair; type why in one line.",
                            picture: "quote-check-answers.png",
                            alt: "The Add it to the quote and Not quoting it buttons ringed",
                        },
                        {
                            title: "When the list is empty, the quote can go",
                            text: "The green line says it matches the test, and Approve & send works again.",
                        },
                        {
                            title: "Changed your mind?",
                            text: 'Tap Undo on a grey "not quoting" line.',
                        },
                    ]}
                />
            </div>
            {open.length > 0 && (
                <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-[13px] text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100">
                    <b>
                        {open.length === 1
                            ? "1 failed device isn't on this quote."
                            : `${open.length} failed devices aren't on this quote.`}
                    </b>
                    {open.map((l) => (
                        <div
                            key={key(l)}
                            className="space-y-2 border-t border-red-200 pt-2 first:border-t-0 first:pt-0 dark:border-red-900"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="font-semibold">{l.device}</div>
                                    <div className="text-[12px] opacity-80">
                                        Failed{l.why ? `: ${l.why}` : ""}
                                        {l.testedAt ? ` · ${shortDate(l.testedAt)} test` : ""}
                                    </div>
                                </div>
                                {editable && (
                                    <div className="flex flex-wrap gap-1.5">
                                        <Button size="sm" disabled={pending} onClick={() => onAdd(l)}>
                                            Add it to the quote
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={pending}
                                            onClick={() => {
                                                setAsking(key(l));
                                                setReason("");
                                            }}
                                        >
                                            Not quoting it…
                                        </Button>
                                    </div>
                                )}
                            </div>
                            {asking === key(l) && (
                                <div className="space-y-1.5">
                                    <label className="block text-[12px] font-semibold" htmlFor={`nq-${key(l)}`}>
                                        Why isn't this one on the quote?
                                    </label>
                                    <textarea
                                        id={`nq-${key(l)}`}
                                        rows={2}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="e.g. Customer is replacing it with their own plumber."
                                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-[13px] text-foreground"
                                    />
                                    <div className="flex justify-end gap-1.5">
                                        <Button size="sm" variant="ghost" onClick={() => setAsking(null)}>
                                            Back
                                        </Button>
                                        <Button
                                            size="sm"
                                            disabled={!reason.trim() || pending}
                                            onClick={() => {
                                                onNotQuoting(l, reason.trim());
                                                setAsking(null);
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {waived.map((l) => (
                <div
                    key={key(l)}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/60 px-3 py-2 text-[12.5px] text-foreground/80"
                >
                    <span>
                        {l.device} · not quoting: {l.notQuotingReason}
                        {l.notQuotingBy ? ` (${l.notQuotingBy})` : ""}
                    </span>
                    {editable && (
                        <Button size="sm" variant="ghost" disabled={pending} onClick={() => onUndo(l)}>
                            Undo
                        </Button>
                    )}
                </div>
            ))}
            {open.length === 0 && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-100">
                    {coverageSummary(legs)}
                </div>
            )}
        </div>
    );
}

export function QuoteCheckStrip({
    estimateId,
    editable,
    onChanged,
}: {
    estimateId: string;
    editable: boolean;
    onChanged?: () => void;
}) {
    const qc = useQueryClient();
    const key = ["close-out-coverage", estimateId];
    const q = useQuery({
        queryKey: key,
        queryFn: () => api.get<{ legs: CoverageLeg[] }>(`/api/close-out/estimates/${estimateId}/coverage`),
    });
    const done = (data: { legs: CoverageLeg[] }) => {
        qc.setQueryData(key, data);
        onChanged?.();
    };
    const fail = (e: unknown) =>
        toast.error((e as { body?: { message?: string } })?.body?.message ?? "Couldn't save that. Try again.");
    const add = useMutation({
        mutationFn: (l: CoverageLeg) =>
            api.post<{ legs: CoverageLeg[] }>(`/api/close-out/estimates/${estimateId}/coverage/add`, {
                assetId: l.assetId,
                leg: l.leg,
            }),
        onSuccess: (d) => {
            done(d);
            toast.success("Added to the quote as Not priced yet. Pick the kit to price it.");
        },
        onError: fail,
    });
    const waive = useMutation({
        mutationFn: ({ l, reason }: { l: CoverageLeg; reason: string }) =>
            api.post<{ legs: CoverageLeg[] }>(`/api/close-out/estimates/${estimateId}/coverage/not-quoting`, {
                assetId: l.assetId,
                leg: l.leg,
                reason,
            }),
        onSuccess: done,
        onError: fail,
    });
    const undo = useMutation({
        mutationFn: (l: CoverageLeg) =>
            api.delete<{ legs: CoverageLeg[] }>(
                `/api/close-out/estimates/${estimateId}/coverage/not-quoting?assetId=${l.assetId}&leg=${l.leg}`,
            ),
        onSuccess: done,
        onError: fail,
    });
    return (
        <QuoteCheckView
            legs={q.data?.legs ?? []}
            editable={editable}
            pending={add.isPending || waive.isPending || undo.isPending}
            onAdd={(l) => add.mutate(l)}
            onNotQuoting={(l, reason) => waive.mutate({ l, reason })}
            onUndo={(l) => undo.mutate(l)}
        />
    );
}
